import { baseService } from 'v2/shared/services/base-services'
import { createGlobalHeaders } from 'v2/shared/services/create-service'
import { getLocalAuth } from 'v2/utils'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { parseSSEFrames } from 'v2/utils/sse-frames'
import { isTerminalUpdate, UPDATE_TYPES, type VibeEvent } from './wire'

/**
 * The ONE stream connection. A logical viewer starts with either POST /generate
 * (deliver input + attach) or GET /sessions/{id}/stream (re-attach), and on any
 * non-abort drop reconnects via the GET viewer from the last cursor with backoff.
 *
 * Cursor rules (the stale-cursor stream_gap bug class, eliminated):
 * - POST /generate NEVER carries Last-Event-ID — a new turn starts a new cursor
 *   sequence; the persisted cursor is cleared before the POST.
 * - Last-Event-ID is sent only on GET viewer opens/reconnects.
 *
 * Detach ≠ cancel: aborting the local reader never touches the server-side run.
 * A resync/re-attach while a reader is live is a no-op (owner keeps its reader).
 */

const BACKOFF_BASE_MS = 500
const BACKOFF_MAX_MS = 15000
// The BE emits a heartbeat every ~25s, so a healthy connection is never silent
// for long. Past ~3 missed heartbeats the socket is presumed half-open (Wi-Fi
// switch, NAT rebind, LB drop with no FIN) — without this, reader.read() blocks
// forever and the reconnect path never fires.
const IDLE_TIMEOUT_MS = 75_000

const computeBackoffMs = (attempt: number): number =>
  Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt - 1))

const abortableSleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) return resolve()
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })

const VIBE_BASE = '/v1/ai-assist/vibe-agent'

const resolveUrl = (path: string): string => {
  const auth = getLocalAuth()
  let base = baseService.instance.defaults.baseURL || ''
  if (base.includes('{{region}}')) {
    if (!auth.region) throw new Error('Region is not set, cannot make request')
    base = base.replace('{{region}}', auth.region)
  }
  return `${base.replace(/\/$/, '')}${path}`
}

export interface SSEError extends Error {
  status?: number
  code?: string
  /** BE-curated human message from the rejection body, safe to render. */
  serverMessage?: string
}

interface OpenSSEOptions {
  method: 'GET' | 'POST'
  body?: unknown
  lastEventId?: string | null
  signal: AbortSignal
  onFrame: (data: VibeEvent, id: string | undefined) => void
}

/** Opens ONE SSE connection. Resolves on normal server close; throws on
 *  HTTP/network error (incl. AbortError on detach). */
const openSSE = async (
  path: string,
  { method, body, lastEventId, signal, onFrame }: OpenSSEOptions,
): Promise<void> => {
  const headers: Record<string, string> = {
    ...createGlobalHeaders(),
    Accept: 'text/event-stream',
  }
  if (lastEventId) headers['Last-Event-ID'] = lastEventId

  const res = await fetch(resolveUrl(path), {
    method,
    headers,
    body: method === 'POST' && body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '')
    const error = new Error(`HTTP ${res.status}: ${text}`) as SSEError
    error.status = res.status
    try {
      const parsed = JSON.parse(text)
      // aiassist rejections: { errors: { global_error: <code> }, message: <human text> }
      const code = parsed?.errors?.global_error ?? parsed?.error
      if (typeof code === 'string') error.code = code
      if (typeof parsed?.message === 'string' && parsed.message) {
        error.serverMessage = parsed.message
      }
    } catch {
      // non-JSON body — status alone drives handling
    }
    throw error
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  // Idle watchdog: any bytes (heartbeats included) re-arm it; on silence past
  // the deadline, cancel the reader — read() resolves done and the normal
  // server-closed reconnect path takes over. Detach/abort is unaffected.
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  const armIdleWatchdog = () => {
    if (idleTimer) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      logger.warn('[VIBE_STREAM] idle timeout — cancelling reader to force reconnect')
      void reader.cancel().catch(() => {})
    }, IDLE_TIMEOUT_MS)
  }
  armIdleWatchdog()
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read()
      if (done) break
      armIdleWatchdog()
      buffer += decoder.decode(value, { stream: true })
      const { frames, rest } = parseSSEFrames(buffer)
      buffer = rest
      for (const frame of frames) {
        if (!frame.data) continue
        try {
          onFrame(JSON.parse(frame.data) as VibeEvent, frame.id)
        } catch (e) {
          logger.error('[VIBE_STREAM] SSE parse error', e, { data: frame.data })
        }
      }
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer)
    reader.releaseLock()
  }
}

export interface StreamCallbacks {
  onEvent: (event: VibeEvent, streamId: string | undefined) => void
  /** Terminal event received, run over. */
  onDone: () => void
  /** Unrecoverable (4xx/409/terminal error) — no viewable run. */
  onError: (err: SSEError) => void
  /** The resume cursor is dead (server stream_gap). The reader has ALREADY
   *  been detached — re-attach from a fresh snapshot cursor. */
  onGap: () => void
}

interface StreamHandle {
  controller: AbortController
  sessionId: string | null
  cursor: string | null
  seenIds: Set<string>
  terminal: boolean
  detached: boolean
  /** Gap backstop: a resync that hands back the same dead cursor must not
   *  spin. Per-run like the rest of the handle — a flow switch resets it. */
  lastGapAt: number
}

let active: StreamHandle | null = null

export const isViewerActive = (): boolean => !!active && !active.terminal && !active.detached

/** Aborts the local reader only — the server-side run continues. */
export const detach = (): void => {
  if (!active) return
  logger.info('[VIBE_STREAM] detaching viewer (run continues server-side)')
  active.detached = true
  active.controller.abort()
  active = null
}

const GAP_BACKOFF_MS = 5000

const runLoop = async (
  handle: StreamHandle,
  first: { method: 'GET' | 'POST'; body?: unknown },
  callbacks: StreamCallbacks,
): Promise<void> => {
  let attempt = 0
  let next = first

  while (!handle.terminal && !handle.detached && !handle.controller.signal.aborted) {
    let sawFrames = false
    try {
      const path =
        next.method === 'POST'
          ? `${VIBE_BASE}/generate`
          : `${VIBE_BASE}/sessions/${handle.sessionId ?? ''}/stream${
              handle.cursor ? `?after=${encodeURIComponent(handle.cursor)}` : ''
            }`

      await openSSE(path, {
        method: next.method,
        body: next.body,
        // Cursor only on GET re-attach — never on a new-turn POST.
        lastEventId: next.method === 'GET' ? handle.cursor : null,
        signal: handle.controller.signal,
        onFrame: (event, id) => {
          sawFrames = true
          if (id) {
            if (handle.seenIds.has(id)) return
            handle.seenIds.add(id)
            handle.cursor = id
          }
          if (!handle.sessionId && event.session_id) handle.sessionId = event.session_id
          if (event.update_type === UPDATE_TYPES.HEARTBEAT) return
          if (event.update_type === UPDATE_TYPES.STREAM_GAP) {
            // The cursor is dead: keeping this reader attached re-gaps on every
            // reconnect while each resync wipes live rows. Kill the reader HERE,
            // where the cursor lives, and let the owner resync + re-attach.
            const now = Date.now()
            if (now - handle.lastGapAt < GAP_BACKOFF_MS) {
              logger.warn('[VIBE_STREAM] stream_gap within backoff window — ignoring')
              return
            }
            handle.lastGapAt = now
            detach()
            callbacks.onGap()
            return
          }
          if (isTerminalUpdate(event.update_type)) handle.terminal = true
          callbacks.onEvent(event, id)
        },
      })

      if (handle.terminal) break
      // Server closed without a terminal event (proxy/idle) — run may still be
      // live; reconnect via the GET viewer from the cursor. Reset the backoff
      // only after a PRODUCTIVE connection: a server that closes cleanly and
      // instantly every time must back off like an erroring one, not hot-loop.
      attempt = sawFrames ? 0 : attempt + 1
      next = { method: 'GET' }
      if (!sawFrames) {
        await abortableSleep(computeBackoffMs(attempt), handle.controller.signal)
      }
    } catch (err) {
      if (handle.detached || handle.controller.signal.aborted) return
      const status = (err as SSEError).status
      if (status && status >= 400 && status < 500) {
        // POST rejected (409 run-in-progress / 4xx) or viewer gone — stop.
        handle.terminal = true
        callbacks.onError(err as SSEError)
        return
      }
      attempt += 1
      next = { method: 'GET' }
      logger.warn('[VIBE_STREAM] reconnecting after drop', { attempt })
      await abortableSleep(computeBackoffMs(attempt), handle.controller.signal)
    }
  }

  if (!handle.detached) {
    active = null
    callbacks.onDone()
  }
}

const start = (
  first: { method: 'GET' | 'POST'; body?: unknown },
  sessionId: string | null,
  cursor: string | null,
  callbacks: StreamCallbacks,
): void => {
  detach()
  const handle: StreamHandle = {
    controller: new AbortController(),
    sessionId,
    cursor,
    seenIds: new Set(),
    terminal: false,
    detached: false,
    lastGapAt: 0,
  }
  active = handle
  void runLoop(handle, first, callbacks)
}

/** New turn: POST /generate (deliver input + attach viewer). No cursor. */
export const startRun = (body: unknown, callbacks: StreamCallbacks): void =>
  start({ method: 'POST', body }, null, null, callbacks)

/** Re-attach to an existing run's durable stream from a cursor. No-op if a
 *  live reader already exists (a resync must never kill an in-flight reader). */
export const attachViewer = (
  sessionId: string,
  cursor: string | null,
  callbacks: StreamCallbacks,
): void => {
  if (isViewerActive()) return
  start({ method: 'GET' }, sessionId, cursor, callbacks)
}
